// Copyright (c) 2019-24 Stax·ai, Inc.
// See LICENSE.md file in project root directory

import React from 'react';
import ContextMenu from '../ContextMenu';
// Styles
import '../../styles/listable.css';
export default class Listable extends React.Component {

    state = {
        sortBy: "",
        sortDir: -1,
        data: [], 
        selected: "", 
    }

    componentDidMount = () => {
        this.setState({ data: [...this.props.data] });
    }

    componentDidUpdate = (prev) => {
        // If we want to preserve locations until re-sorted (in case we're editing in place), then pass the prop: "preserveLocationsByKey: _id" with the unique identifier that will never be changed.
        if (this.props.preserveLocationsByKey) {
            if (JSON.stringify(prev.data?.map(d => d[this.props.preserveLocationsByKey])) !== JSON.stringify(this.props.data?.map(d => d[this.props.preserveLocationsByKey]))) {
                this.sort();
            }
            return;
        }

        // Check deep if data has changed (not just reference)
        if (JSON.stringify(prev.data) !== JSON.stringify(this.props.data))
            this.sort();
    }

    sortAndReload = (sortBy, sortDir) => {
        this.setState({ sortBy, sortDir }, () => this.sort());
    }

    sort = () => {
        let { sortBy, sortDir } = this.state;
        if(sortBy === "")
            return this.setState({ data: this.props.data })

        // Get anything that is pinned...  
        let pinned = this.props.data.filter(d => d.pinned);
        let unpinned = this.props.data.filter(d => !d.pinned); 

        const header = this.props.columns.find(c => c.prop === sortBy); 

        pinned.sort((a,b) => {
            const compareA = header?.sortComparitor?.(a)??a[sortBy];
            const compareB = header?.sortComparitor?.(b)??b[sortBy];
            if (!compareA && !compareB) {
                return 0;
            }
            else if (!compareA || !compareB) {
                return !compareA ? -sortDir : sortDir;
            }

            return compareA > compareB ? sortDir : compareA < compareB ? -sortDir : 0;
        });
        unpinned.sort((a,b) => {
            const compareA = header?.sortComparitor?.(a)??a[sortBy];
            const compareB = header?.sortComparitor?.(b)??b[sortBy];
            if (!compareA && !compareB) {
                return 0;
            }
            else if (!compareA || !compareB) {
                return !compareA ? -sortDir : sortDir;
            }

            return compareA > compareB ? sortDir : compareA < compareB ? -sortDir : 0
        });
        // Join the arrays 
        let sorted = [...pinned, ...unpinned];
        this.setState({ data: sorted }); 
        if(this.props.onSort && sorted)
            this.props.onSort(sorted); 
    }

    toggleHeader = (key) => async () => {
        if (this.props.disableSort) return;
        const sortBy = key;
        const sortDir = this.state.sortBy === key ? (this.state.sortDir > 0 ? -1 : 1) : 1;
        this.setState({ sortBy, sortDir }, () => {
            this.sort(); 
        });
    }

    getSortArrow = (key) => {
        if (this.state.sortBy !== key)
            return "";

        return this.state.sortDir > 0 ? <>▴&nbsp;</> : <>▾&nbsp;</>;
    }

    selectItem = (item) => {
        let selected = this.state.selected;
        if (selected === item._id)
            selected = "";
        else
            selected = item._id; 
        this.setState({ selected });
    }

    isSelected = (item) => {
        return this.state.selected === item._id;
    }

    render = () => {
        let content = [...this?.state?.data??Array(this.props.pageSize).fill({})]; 
        // Do we need to slice this based on page? 
        if('page' in this.props && this.props.pageSize && !this.props?.dynamicHeight){
            content = content.slice(this.props.page * this.props.pageSize, (this.props.page + 1) * this.props.pageSize);
        } 

        return (
            <div
                className='listable-container'
                style={{...(this.props?.styles || {}), paddingBottom: 100}}
            >
                <div
                    className='listable-headers'
                    style={{
                        ...(this.props.stickyHeader? {position: 'sticky'}:{}),
                        ...(this.props.stickyHeader? {top: -1}:{}),
                    }}
                >
                    {
                        this.props.columns.map((column, idx) => {
                            return (
                                <div
                                    key={idx} 
                                    className={`listable-header ${column.sortable? 'sortable': ''}`}
                                    style={{
                                        ...(column.width?{minWidth:column.width, width: column.width}: {}),
                                        ...(column.align?{textAlign:column.align}: {}),
                                        ...(column.cursor?{cursor:column.cursor}:{cursor: 'pointer'}),
                                        ...(column.marginRight?{marginRight:column.marginRight}:{}),
                                        ...(column.grow === false?{flexGrow: 0}:{flexGrow: 1}),
                                    }}
                                    onClick={column.disableSort? () => {}: this.toggleHeader(column.prop)}
                                >
                                    <div className='a' style={{color: 'inherit', fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: column.align? column.align === 'right'? 'end': 'start': 'start'}}>
                                    {this.getSortArrow(column.prop)} {column.header}  
                                    </div>
                                </div>
                            )
                        })
                    }
                </div>
                <div
                    className='listable-rows-container'
                    id='listable-rows'
                >
                    {
                        content.length? content.map((item, idx) => {
                            // Is this item selected? 
                            const isSelected = this.isSelected(item); 
                            return <div
                                key={idx}
                                className={`listable-row ${item.pinned? 'pinned': ''} ${isSelected? 'selected': ''} ${item.disabled? 'disabled': ''}`}
                                onClick={(e) => {
                                    if(this.props.loading)
                                        return; 
                                    
                                    if(this.props.onRowClick && typeof this.props.onRowClick === 'function' && !(e.target.onclick instanceof Function)) {
                                        this.props.onRowClick(item, e);
                                    }
                                }}
                                onDoubleClick={(e) => {
                                    if(this.props.onRowDoubleClick && typeof this.props.onRowDoubleClick === 'function') {
                                        this.props.onRowDoubleClick(item);
                                    }
                                }}
                                onContextMenu={(e) => {
                                    if(!this.props.contextMenuOptions)
                                        return 
                                    this.selectItem(item); 
                                    this.openMenu(e, {...item, copyValue: e.target.innerText});
                                }}
                            >
                                {this.props.columns.map((column, index) => {
                                    
                                    return (
                                        <div
                                            key={index}
                                            className='listable-cell'
                                            style={{
                                                ...(column.width?{minWidth:column.width,width: column.width}: {}),
                                                ...(column.align?{textAlign:column.align}: {}),
                                                ...(column.cursor?{cursor:column.cursor}: {}),
                                                ...(column.marginRight?{marginRight:column.marginRight}: {}),
                                                ...(column.overflow?{overflow: 'visible', height: 38,}: {}),
                                                ...(column.textOverflow?{textOverflow: column.textOverflow}: {}),
                                                ...(column.grow === false?{flexGrow: 0}:{flexGrow: 1}),
                                                ...(this.props.debug? {backgroundColor: index%2===0? 'red': 'green'}: {}),
                                                position: 'relative',
                                            }}
                                        >
                                            {
                                                this.props.loading?
                                                <div 
                                                    className='listable-loading'
                                                    style={{
                                                        width: '100%',
                                                        height: '20px',
                                                        backgroundColor: 'var(--color-menu-raised)',
                                                    }}
                                                />
                                                :
                                                column?.render?.(item, idx)??item[column.prop]?? '-'
                                            }
                                        </div>
                                    )
                                })}
                            </div>
                        })
                        :
                        <div
                            style={{
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                color: 'var(--color-text-light)',
                                paddingTop: 30,
                                paddingBottom: 30
                            }}
                        >
                            {this.props.emptyMessage??"Nothing to show..."}
                        </div>
                    }
                </div>
                <ContextMenu
                    load={(open,close) => {
                        this.openMenu = open;
                        this.closeMenu = close;
                    }}
                    options={this.props?.contextMenuOptions??[]}
                    onClose={() => this.setState({ selected: [] })} 
                />
            </div>
        );
    }
}